import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { setFlag } from "@/lib/first-steps-flags";
import { Plus, MapPin, ShoppingBag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { MrDriveAvatar } from "@/components/MrDriveAvatar";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DEAL_FILTER,
  DEAL_TYPES,
  DEAL_TYPE_LABEL,
  DEAL_TYPE_TONE,
  ALL_CATEGORIES,
  formatPrice,
  timeAgo,
  type DealType,
  type MarketplaceListing,
} from "@/lib/marketplace-constants";

type Author = { user_id: string; full_name: string; photo_url: string | null };

async function fetchListings(): Promise<MarketplaceListing[]> {
  const { data, error } = await supabase
    .from("marketplace_listings")
    .select("*")
    .eq("status", "active")
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as MarketplaceListing[];
}

export const Route = createFileRoute("/_authenticated/marketplace")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["marketplace", "listings"],
      queryFn: fetchListings,
    });
  },
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-white px-4 py-12">
      <div className="mx-auto max-w-md text-center">
        <h1 className="text-xl font-semibold text-red-600">Не удалось загрузить Барахолку</h1>
        <p className="mt-2 text-sm text-muted-foreground">{(error as Error).message}</p>
      </div>
    </div>
  ),
  notFoundComponent: () => (
    <div className="min-h-screen bg-white px-4 py-12 text-center">Не найдено.</div>
  ),
  component: MarketplacePage,
});

type Tab = "feed" | "mine" | "responses";

function MarketplacePage() {
  const { user } = useAuth();
  useEffect(() => {
    if (user?.id) setFlag(user.id, "market_open");
  }, [user?.id]);
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("feed");
  const [dealFilter, setDealFilter] = useState<string>("Все");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  const { data: listings = [] } = useSuspenseQuery({
    queryKey: ["marketplace", "listings"],
    queryFn: fetchListings,
  });

  const { data: mine = [] } = useQuery({
    queryKey: ["marketplace", "mine", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("marketplace_listings")
        .select("*")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as MarketplaceListing[];
    },
  });

  const { data: myResponseListings = [] } = useQuery({
    queryKey: ["marketplace", "my-responses", user?.id],
    enabled: !!user?.id && tab === "responses",
    queryFn: async () => {
      const { data: resps, error } = await supabase
        .from("marketplace_listing_responses")
        .select("listing_id, created_at")
        .eq("sender_user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const ids = Array.from(new Set((resps ?? []).map((r) => r.listing_id)));
      if (!ids.length) return [] as MarketplaceListing[];
      const { data: ls, error: e2 } = await supabase
        .from("marketplace_listings")
        .select("*")
        .in("id", ids);
      if (e2) throw e2;
      const byId = new Map((ls ?? []).map((l) => [l.id, l as MarketplaceListing]));
      const ordered: MarketplaceListing[] = [];
      for (const r of resps ?? []) {
        const l = byId.get(r.listing_id);
        if (l && !ordered.find((x) => x.id === l.id)) ordered.push(l);
      }
      return ordered;
    },
  });

  const sourceListings =
    tab === "feed" ? listings : tab === "mine" ? mine : myResponseListings;

  const filtered = useMemo(() => {
    return sourceListings.filter((l) => {
      if (tab === "feed" && dealFilter !== "Все") {
        const label = DEAL_TYPE_LABEL[l.deal_type];
        if (label !== dealFilter) return false;
      }
      if (tab === "feed" && categoryFilter !== "all" && l.category !== categoryFilter) return false;
      return true;
    });
  }, [sourceListings, dealFilter, categoryFilter, tab]);

  const authorIds = useMemo(
    () => Array.from(new Set(filtered.map((l) => l.user_id))),
    [filtered],
  );

  const { data: authors = {} } = useQuery({
    queryKey: ["marketplace", "authors", authorIds.sort().join(",")],
    enabled: authorIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, full_name, photo_url")
        .in("user_id", authorIds);
      if (error) throw error;
      const map: Record<string, Author> = {};
      for (const a of data ?? []) map[a.user_id] = a as Author;
      return map;
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <main className="mx-auto max-w-[1120px] px-5 py-8 md:px-6 md:py-12">
        <div className="mb-6 sm:flex sm:items-start sm:justify-between sm:gap-3">
          <div className="flex items-start gap-3 sm:min-w-0 sm:flex-1">
            <div className="min-w-0 flex-1">
              <h1 className="font-serif text-3xl text-td-dark md:text-4xl">Барахолка</h1>
              <p className="mt-2 max-w-xl text-sm text-td-muted md:text-base">
                Покупай, продавай, отдавай и обменивайся вещами внутри сообщества TurkeyDrive.
              </p>
            </div>
            <div className="sm:hidden">
              <MrDriveAvatar variant="marketplace" size={108} />
            </div>
          </div>
          <div className="hidden sm:block">
            <MrDriveAvatar variant="marketplace" size={168} />
          </div>
          <Button
            onClick={() => navigate({ to: "/marketplace/new" })}
            className="mt-4 w-full shrink-0 bg-td-teal text-white hover:bg-td-teal/90 sm:mt-0 sm:w-auto"
          >
            <Plus className="mr-1 h-4 w-4" />
            Разместить объявление
          </Button>
        </div>

        <div className="mb-5 flex gap-1 rounded-full bg-td-soft/60 p-1 text-sm">
          {[
            { k: "feed" as Tab, l: "Лента" },
            { k: "mine" as Tab, l: "Мои объявления" },
            { k: "responses" as Tab, l: "Мои отклики" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`flex-1 rounded-full px-3 py-1.5 font-medium transition ${
                tab === t.k ? "bg-td-teal text-white" : "text-td-muted hover:text-td-dark"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "feed" && (
          <div className="mb-5 space-y-3">
            <div className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory">
              {DEAL_FILTER.map((f) => (
                <button
                  key={f}
                  onClick={() => setDealFilter(f)}
                  className={`snap-start shrink-0 rounded-full border px-4 py-1.5 text-xs font-semibold transition ${
                    dealFilter === f
                      ? "border-td-teal bg-td-teal text-white"
                      : "border-td-line bg-white text-td-dark hover:border-td-teal"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-72">
                <SelectValue placeholder="Все категории" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все категории</SelectItem>
                {ALL_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="mt-4 text-sm text-td-muted">{pluralListings(filtered.length)}</div>

        {filtered.length === 0 ? (
          <EmptyState tab={tab} onCreate={() => navigate({ to: "/marketplace/new" })} />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((l) => (
              <ListingCard key={l.id} listing={l} author={authors[l.user_id]} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function EmptyState({ tab, onCreate }: { tab: Tab; onCreate: () => void }) {
  const messages: Record<Tab, string> = {
    feed: "Пока нет объявлений. Стань первым!",
    mine: "У тебя ещё нет объявлений.",
    responses: "Ты ещё не откликался ни на одно объявление.",
  };
  return (
    <div className="rounded-2xl border border-dashed border-td-line bg-td-soft/30 p-10 text-center">
      <ShoppingBag className="mx-auto h-10 w-10 text-td-muted" />
      <p className="mt-3 text-sm text-td-muted">{messages[tab]}</p>
      {tab !== "responses" && (
        <Button onClick={onCreate} className="mt-4 bg-td-teal text-white hover:bg-td-teal/90">
          + Разместить объявление
        </Button>
      )}
    </div>
  );
}

function ListingCard({ listing, author }: { listing: MarketplaceListing; author?: Author }) {
  const cover = listing.photos?.[0];
  const priceText =
    listing.deal_type === "продам"
      ? formatPrice(listing.price, listing.currency) || "Цена не указана"
      : listing.deal_type === "отдам"
      ? "Бесплатно"
      : listing.deal_type === "ищу" && listing.budget != null
      ? `Бюджет: ${formatPrice(listing.budget, listing.currency)}`
      : DEAL_TYPE_LABEL[listing.deal_type];

  return (
    <Link
      to="/marketplace/$listingId"
      params={{ listingId: listing.id }}
      className="group block overflow-hidden rounded-2xl border border-td-line bg-white transition hover:border-td-teal hover:shadow-md"
    >
      <div className="relative aspect-[4/3] w-full bg-td-soft">
        {cover ? (
          <img loading="lazy" decoding="async" src={cover} alt={listing.title} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <ShoppingBag className="h-10 w-10 text-td-muted/50" />
          </div>
        )}
        <span
          className={`absolute left-2 top-2 rounded-full px-2 py-0.5 text-[11px] font-semibold ${DEAL_TYPE_TONE[listing.deal_type]}`}
        >
          {DEAL_TYPE_LABEL[listing.deal_type]}
        </span>
      </div>
      <div className="p-3">
        <div className="text-[11px] font-medium uppercase tracking-wide text-td-muted">
          {listing.category}
        </div>
        <h3 className="mt-1 line-clamp-2 text-sm font-semibold text-td-dark group-hover:text-td-teal">
          {listing.title}
        </h3>
        <div className="mt-1.5 text-sm font-bold text-td-dark">{priceText}</div>
        <div className="mt-2 flex items-center gap-1 text-xs text-td-muted">
          <MapPin className="h-3 w-3" />
          <span className="truncate">{listing.city}{listing.district ? `, ${listing.district}` : ""}</span>
        </div>
        {author && (
          <AuthorMini author={author} createdAt={listing.created_at} />
        )}
      </div>
    </Link>
  );
}

function AuthorMini({ author, createdAt }: { author: Author; createdAt: string }) {
  const initials = (author.full_name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div className="mt-2 flex items-center gap-2 border-t border-td-line pt-2">
      <Link
        to="/people/$userId"
        params={{ userId: author.user_id }}
        onClick={(e) => e.stopPropagation()}
        className="flex min-w-0 items-center gap-2 hover:text-td-teal"
      >
        {author.photo_url ? (
          <img loading="lazy" decoding="async" src={author.photo_url} alt="" className="h-6 w-6 rounded-full object-cover" />
        ) : (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-td-soft text-[10px] font-semibold text-td-teal">
            {initials}
          </div>
        )}
        <span className="truncate text-xs font-medium text-td-dark">{author.full_name}</span>
      </Link>
      <span className="ml-auto text-[11px] text-td-muted">{timeAgo(createdAt)}</span>
    </div>
  );
}

function pluralListings(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} объявление`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${n} объявления`;
  return `${n} объявлений`;
}
